import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  AreaChart,
  BarChart3,
  Columns3,
  Database,
  Download,
  FileSpreadsheet,
  LayoutDashboard,
  Maximize2,
  PieChart,
  Rows3,
  Sigma,
  Table2,
  TrendingUp,
} from 'lucide-react';
import TableDisplay from './TableDisplay';
import ChartDisplay from './ChartDisplay';
import ChartModal from './ChartModal';
import { Column, Row, ChartType, DataPoint } from './types';
import { formatChartValue, parseNumericValue, parseTableData } from './dataUtils';

interface DataVisualizationProps {
  columns: Column[];
  rows: Row[];
}

type ViewMode = 'analysis' | 'table' | 'chart';

const chartOptions: { type: ChartType; icon: React.ReactNode; label: string }[] = [
  { type: 'bar', icon: <BarChart3 className="h-4 w-4" />, label: 'Barras' },
  { type: 'line', icon: <TrendingUp className="h-4 w-4" />, label: 'Linha' },
  { type: 'area', icon: <AreaChart className="h-4 w-4" />, label: 'Área' },
  { type: 'pie', icon: <PieChart className="h-4 w-4" />, label: 'Pizza' },
];

const formatNumber = (value: number) =>
  Number.isFinite(value)
    ? value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
    : '-';

const escapeCsvValue = (value: string) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const getExportFileName = (extension: 'csv' | 'xlsx') =>
  `compra-ai-dados-${new Date().toISOString().slice(0, 10)}.${extension}`;

const buildColumnWidths = (columns: Column[], rows: Row[]) =>
  columns.map((column, index) => {
    const sampleValues = rows.slice(0, 100).map((row) => row.values[index]?.str?.length || 0);
    const width = Math.max(column.name.length + 2, 12, ...sampleValues.map((length) => length + 2));
    return { wch: Math.min(width, 48) };
  });

const getMetricValue = (point: DataPoint, metric: string) => {
  const value = Number(point[metric] ?? point.value ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const AiDataVisualization: React.FC<DataVisualizationProps> = ({ columns, rows }) => {
  const parsedData = useMemo(() => parseTableData(columns, rows), [columns, rows]);
  const [view, setView] = useState<ViewMode>(parsedData.canChart ? 'analysis' : 'table');
  const [chartType, setChartType] = useState<ChartType>(parsedData.suggestedChart);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setChartType(parsedData.suggestedChart);
    setView(parsedData.canChart ? 'analysis' : 'table');
  }, [parsedData.canChart, parsedData.suggestedChart]);

  const activeView: ViewMode = parsedData.canChart ? view : 'table';
  const primaryMetric = parsedData.valueKeys[0] || '';
  const primaryMetricIndex = parsedData.numericColumnIndexes[0] ?? -1;
  const labelColumnIndex = parsedData.labelColumnIndex ?? 0;

  const insightData = useMemo(() => {
    if (!parsedData.canChart || !primaryMetric || primaryMetricIndex < 0) {
      return {
        total: 0,
        average: 0,
        maxPoint: null as DataPoint | null,
        rankedData: [] as DataPoint[],
      };
    }

    const allMetricPoints: DataPoint[] = rows.map((row) => {
      const value = parseNumericValue(row.values[primaryMetricIndex]?.str || '0');
      return {
        name: row.values[labelColumnIndex]?.str || 'Sem identificação',
        value,
        [primaryMetric]: value,
      };
    });
    const rankedData = [...allMetricPoints].sort(
      (a, b) => getMetricValue(b, primaryMetric) - getMetricValue(a, primaryMetric)
    );
    const total = allMetricPoints.reduce(
      (sum, item) => sum + getMetricValue(item, primaryMetric),
      0
    );
    const average = allMetricPoints.length ? total / allMetricPoints.length : 0;

    return {
      total,
      average,
      maxPoint: rankedData[0] || null,
      rankedData,
    };
  }, [labelColumnIndex, parsedData.canChart, primaryMetric, primaryMetricIndex, rows]);

  const handleExportCSV = () => {
    const headers = columns.map((column) => escapeCsvValue(column.name)).join(',');
    const csvRows = rows.map((row) =>
      columns
        .map((_column, index) => escapeCsvValue(row.values[index]?.str || ''))
        .join(',')
    );
    const csv = [headers, ...csvRows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getExportFileName('csv');
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportXLSX = () => {
    const workbook = XLSX.utils.book_new();
    const sheetData = [
      columns.map((column) => column.name),
      ...rows.map((row) => columns.map((_column, index) => row.values[index]?.str || '')),
    ];
    const dataSheet = XLSX.utils.aoa_to_sheet(sheetData);

    dataSheet['!cols'] = buildColumnWidths(columns, rows);
    XLSX.utils.book_append_sheet(workbook, dataSheet, 'Dados');

    const summaryRows = [
      { Indicador: 'Registros', Valor: rows.length },
      { Indicador: 'Colunas', Valor: columns.length },
      { Indicador: 'Dimensão analisada', Valor: parsedData.labelKey || '-' },
      { Indicador: 'Métricas numéricas', Valor: parsedData.valueKeys.join(', ') || '-' },
      { Indicador: 'Amostra dos gráficos', Valor: parsedData.chartData.length },
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
    summarySheet['!cols'] = [{ wch: 24 }, { wch: 48 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumo');

    XLSX.writeFile(workbook, getExportFileName('xlsx'));
  };

  const openChart = (type: ChartType) => {
    setChartType(type);
    setModalOpen(true);
  };

  const chartCards = [
    {
      type: 'bar' as ChartType,
      title: 'Comparativo',
      subtitle: `${primaryMetric || 'Métrica'} por ${parsedData.labelKey || 'categoria'}`,
    },
    {
      type: 'line' as ChartType,
      title: 'Evolução',
      subtitle: 'Leitura sequencial dos registros',
    },
    {
      type: 'area' as ChartType,
      title: 'Volume',
      subtitle: 'Concentração visual da métrica',
    },
    {
      type: 'pie' as ChartType,
      title: 'Distribuição',
      subtitle: primaryMetric || 'Participação por categoria',
    },
  ];

  return (
    <>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <Database className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">
                Resultado da consulta
              </p>
              <h3 className="truncate text-base font-semibold text-gray-900">
                {rows.length.toLocaleString('pt-BR')} registros analisados
              </h3>
              <p className="truncate text-xs text-gray-500">
                {columns.length.toLocaleString('pt-BR')} colunas
                {parsedData.canChart && parsedData.labelKey ? ` | dimensão: ${parsedData.labelKey}` : ''}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleExportCSV}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
              title="Exportar CSV"
            >
              <Download className="h-4 w-4" />
              CSV
            </button>
            <button
              onClick={handleExportXLSX}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
              title="Exportar XLSX"
            >
              <FileSpreadsheet className="h-4 w-4" />
              XLSX
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center overflow-x-auto rounded-lg border border-gray-200 bg-white p-1">
            {parsedData.canChart && (
              <button
                onClick={() => setView('analysis')}
                className={`inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-semibold transition ${
                  activeView === 'analysis'
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                aria-pressed={activeView === 'analysis'}
              >
                <LayoutDashboard className="h-4 w-4" />
                Análise
              </button>
            )}
            <button
              onClick={() => setView('table')}
              className={`inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-semibold transition ${
                activeView === 'table'
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              aria-pressed={activeView === 'table'}
            >
              <Table2 className="h-4 w-4" />
              Tabela
            </button>
            {parsedData.canChart && (
              <button
                onClick={() => setView('chart')}
                className={`inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-semibold transition ${
                  activeView === 'chart'
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                aria-pressed={activeView === 'chart'}
              >
                <BarChart3 className="h-4 w-4" />
                Gráfico
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1.5 ring-1 ring-gray-200">
              <Rows3 className="h-3.5 w-3.5 text-blue-600" />
              {rows.length.toLocaleString('pt-BR')} linhas
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1.5 ring-1 ring-gray-200">
              <Columns3 className="h-3.5 w-3.5 text-violet-600" />
              {columns.length.toLocaleString('pt-BR')} colunas
            </span>
            {parsedData.canChart && (
              <span className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1.5 ring-1 ring-gray-200">
                <Sigma className="h-3.5 w-3.5 text-emerald-600" />
                {parsedData.valueKeys.length.toLocaleString('pt-BR')} métricas
              </span>
            )}
          </div>
        </div>

        <div className="bg-white p-4">
          {activeView === 'analysis' && parsedData.canChart && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <InsightCard
                  icon={<Rows3 className="h-4 w-4" />}
                  label="Registros"
                  value={rows.length.toLocaleString('pt-BR')}
                  tone="blue"
                />
                <InsightCard
                  icon={<Sigma className="h-4 w-4" />}
                  label={primaryMetric || 'Métrica'}
                  value={formatChartValue(insightData.total)}
                  tone="emerald"
                />
                <InsightCard
                  icon={<TrendingUp className="h-4 w-4" />}
                  label="Média"
                  value={formatNumber(insightData.average)}
                  tone="amber"
                />
                <InsightCard
                  icon={<BarChart3 className="h-4 w-4" />}
                  label="Maior valor"
                  value={formatChartValue(
                    insightData.maxPoint ? getMetricValue(insightData.maxPoint, primaryMetric) : 0
                  )}
                  detail={insightData.maxPoint?.name}
                  tone="rose"
                />
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                {chartCards.map((card) => (
                  <div key={card.type} className="rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
                      <div className="min-w-0">
                        <h4 className="truncate text-sm font-semibold text-gray-900">{card.title}</h4>
                        <p className="truncate text-xs text-gray-500">{card.subtitle}</p>
                      </div>
                      <button
                        onClick={() => openChart(card.type)}
                        className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                        title="Expandir gráfico"
                        aria-label="Expandir gráfico"
                      >
                        <Maximize2 className="h-4 w-4" />
                      </button>
                    </div>
                    <ChartDisplay
                      data={parsedData.chartData}
                      chartType={card.type}
                      valueKeys={parsedData.valueKeys}
                      labelKey={parsedData.labelKey}
                      height={270}
                    />
                  </div>
                ))}
              </div>

              {insightData.rankedData.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Destaques</h4>
                      <p className="text-xs text-gray-500">{primaryMetric}</p>
                    </div>
                    <span className="rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-gray-600 ring-1 ring-gray-200">
                      Top {Math.min(5, insightData.rankedData.length)}
                    </span>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-5">
                    {insightData.rankedData.slice(0, 5).map((item, index) => (
                      <div key={`${item.name}-${index}`} className="rounded-lg bg-white p-3 ring-1 ring-gray-200">
                        <p className="truncate text-xs font-medium text-gray-500" title={item.name}>
                          {index + 1}. {item.name}
                        </p>
                        <p className="mt-1 truncate text-sm font-semibold text-gray-900">
                          {formatChartValue(getMetricValue(item, primaryMetric))}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeView === 'table' && <TableDisplay columns={columns} rows={rows} />}

          {activeView === 'chart' && parsedData.canChart && (
            <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Gráfico principal</h4>
                  <p className="text-xs text-gray-500">
                    {primaryMetric || 'Métrica'} por {parsedData.labelKey || 'categoria'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 p-1">
                    {chartOptions.map((option) => (
                      <button
                        key={option.type}
                        onClick={() => setChartType(option.type)}
                        className={`inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-xs font-semibold transition ${
                          chartType === option.type
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-600 hover:bg-white'
                        }`}
                        title={option.label}
                        aria-pressed={chartType === option.type}
                      >
                        {option.icon}
                        <span className="hidden sm:inline">{option.label}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setModalOpen(true)}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                    title="Expandir gráfico"
                  >
                    <Maximize2 className="h-4 w-4" />
                    Expandir
                  </button>
                </div>
              </div>
              <ChartDisplay
                data={parsedData.chartData}
                chartType={chartType}
                valueKeys={parsedData.valueKeys}
                labelKey={parsedData.labelKey}
                height={380}
              />
            </div>
          )}
        </div>
      </div>

      {parsedData.canChart && (
        <ChartModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          data={parsedData.chartData}
          initialChartType={chartType}
          valueKeys={parsedData.valueKeys}
          labelKey={parsedData.labelKey}
        />
      )}
    </>
  );
};

type InsightCardProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail?: string;
  tone: 'blue' | 'emerald' | 'amber' | 'rose';
};

const toneClasses: Record<InsightCardProps['tone'], string> = {
  blue: 'bg-blue-50 text-blue-700 ring-blue-100',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  amber: 'bg-amber-50 text-amber-700 ring-amber-100',
  rose: 'bg-rose-50 text-rose-700 ring-rose-100',
};

const InsightCard: React.FC<InsightCardProps> = ({ icon, label, value, detail, tone }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-xs font-semibold text-gray-500" title={label}>
          {label}
        </p>
        <p className="mt-1 truncate text-lg font-semibold text-gray-900" title={value}>
          {value}
        </p>
        {detail && (
          <p className="mt-1 truncate text-xs text-gray-500" title={detail}>
            {detail}
          </p>
        )}
      </div>
      <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ring-1 ${toneClasses[tone]}`}>
        {icon}
      </div>
    </div>
  </div>
);

export default AiDataVisualization;
